import React from "react";
import type ResponsiveImage from "../../components/responsive-image/responsive-image.ts";

export interface Room {
  name: string;
  description?: string;
  width?: string;
  length?: string;
}

export interface School {
  id: number;
  name?: string;
  address: string;
  lat: number;
  lng: number;
  catchmentSearchLink: string;
}

export interface PropertyDetailsContextValue {
  // Identity / display
  propertyId?: number;
  propertySource?: string;
  // Back-office reference used as the reporting key (distinct from the display displayReference).
  propertyReference?: number;
  displayReference?: string;
  mainTitle?: string;
  displayAddress?: string;
  description?: string;
  viewingDescription?: string;
  price?: number;
  offerType?: string;
  lat?: number;
  lon?: number;
  // Canonical path of the property page, used for share links.
  shareUrl?: string;

  // Utilities
  maxSpeed?: number | null;
  mobileSignalCoverage?: { provider: string; signalStrength: number }[] | null;
  serviceCharge?: number | null;
  groundRent?: number | null;
  sharedOwnership?: number | null;

  // Key property facts
  beds?: number | null;
  bathrooms?: number | null;
  receptionRooms?: number | null;
  metricArea?: number | null;
  epc?: string | null;
  councilTaxBand?: string | null;
  tenure?: string | null;
  rental?: boolean;
  leaseLength?: number | null;
  deposit?: number | null;
  highlight?: string;

  // Status / tags
  newAdd?: boolean;
  exclusive?: boolean;
  underOffer?: boolean;
  closingText?: string | null;
  newBuild?: boolean;
  premium?: boolean;
  featured?: boolean;
  video?: boolean;
  virtualTour?: boolean;
  isWatchlisted?: boolean;

  // Agent
  solicitorId?: number;
  agentLogoUrl?: string;
  agentName?: string;
  agentPhone?: string;
  agentAddress?: string;
  agentEmail?: string;
  chartered?: boolean;
  agentPageUrl?: string;

  // Website Contact
  websiteContactName?: string;
  websiteContactEmail?: string;
  websiteContactPhone?: string;
  websiteContactPhotoUrl?: string;

  hrLink?: string;
  scheduleLink?: string;
  digitalBrochureLink?: string;

  // Schools
  schools?: School[];

  // District stats
  districtStatsLocation?: string;
  averageSellingPrice?: number;
  medianDaysToSell?: number;
  averagePercentOfHomeReportAchieved?: number | null;
  mostPopularPropertyType?: string;
  isUsingParentAreaStats?: boolean;
  parentAreaName?: string;
  districtStatsEndOfPeriod?: string;
  districtStatsLengthOfPeriodMonths?: string;

  // Rooms
  rooms?: Room[];
  features?: string[];

  // Hero media
  images?: ResponsiveImage[];
  virtualTourUrl?: string;
  isVirtualTourExternalRedirect?: boolean;
  isVideoExternalRedirect?: boolean;
  videoUrl?: string;
  floorPlanResourceUrl?: string;

  // Links
  usefulLinks?: LinkItem[];
  soldNearbyLinks?: LinkItem[];
  backLink?: string;

  isAuthenticated?: boolean;

  //Ad targeting
  adTargeting?: Record<string, string[]>;

  // Preview
  preview?: Preview;
}

// Mirrors the PreviewStatus enum in Features/Property/PropertyPage/Models/PreviewStatus.cs,
// which is serialized by name via JsonStringEnumConverter.
export type PreviewStatus = "Expired" | "Valid" | "WaitingForUpdate";

export interface Preview {
  status: PreviewStatus;
  message?: string;
  lastUpdatedDate?: string;
}

export interface LinkItem {
  label: string;
  href: string;
}

export const PropertyDetailsContext = React.createContext<PropertyDetailsContextValue>({});

export const PropertyDetailsProvider: React.FC<{
  value: PropertyDetailsContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <PropertyDetailsContext.Provider value={value}>{children}</PropertyDetailsContext.Provider>
);

export const usePropertyDetailsContext = () => React.useContext(PropertyDetailsContext);
